/* eslint-disable @typescript-eslint/no-explicit-any */

import { KuxErrorType } from '@kaltura-ott/tvpil';
import { createErrorEvent, tvpilJournal } from '@kaltura-ott/tvpil-utils';
import { CancelledError } from '@tanstack/query-core';
import { container } from 'tsyringe';

import { ErrorPriority } from '../stores/error/error-priority';
import { AppApiErrorList, ErrorStore } from '../stores/error/error-store';

type HandlerFunction = (error: KuxErrorType, ctx: any) => void;

// const API_RETRY_TIMEOUT = 3000;
export const NETWORK_ERROR = 'client::request-network-error';

/**
 * @category Utils
 */
async function _handleError(
  id: number,
  ctx: any,
  descriptor: any,
  args: any,
  errorType: any,
  error: KuxErrorType,
  type: ErrorPriority,
  argsCount: number,
  actionName: string,
  moduleName: string,
  callback?: HandlerFunction,
  skipRetry?: boolean,
  prevResolveRetry?: () => void,
) {
  return new Promise((resolve, reject) => {
    const errorStore = container.resolve(ErrorStore);

    if (!errorStore || errorStore?.innerApiError === undefined) return;

    if (error instanceof CancelledError) {
      return;
    }

    if (typeof callback === 'function' && error instanceof errorType) {
      // Run handler with error object and class context
      callback.call(ctx, error, ctx);
    }

    tvpilJournal.log(createErrorEvent('HandleError', { error }));

    if (type === ErrorPriority.low) {
      return;
    }

    const onRetry = () =>
      // eslint-disable-next-line no-async-promise-executor
      new Promise(async (resolveRetry) => {
        tvpilJournal.log(createErrorEvent('Retry', { id }));

        errorStore.isRetrying = true;

        const undefinedArgs = Array.from(Array(Math.max(0, argsCount - args.length)));
        const result = await descriptor.value.apply(ctx, [...args, ...undefinedArgs, `apiErrorId:${id}`, resolveRetry]);
        errorStore.isRetrying = false;
        resolve(result);

        if (errorStore.innerApiError?.[id]) {
          errorStore.clearApiError(id);
        }
      });

    errorStore.isRetrying = false;
    const retryAttempts = skipRetry
      ? 0
      : ((errorStore.innerApiError && errorStore.innerApiError[id]?.retryAttempts) || 0) + 1;

    const innerApiError = {
      id,
      actionName,
      moduleName,
      title: error?.name,
      code: error?.httpStatus || error?.code || (error?.status === 0 ? NETWORK_ERROR : error?.status),
      message: error?.message,
      error: error,
      type,
      onRetry,
      onCancel: () => {
        errorStore.clearAllApiError();
        reject();
      },
      retryAttempts: retryAttempts,
    };

    if (!errorStore.innerApiError) {
      errorStore.innerApiError = {
        [id]: innerApiError,
      };
    } else {
      const highPriorityErrorKey = Object.keys(errorStore.innerApiError).find(
        (key) => errorStore.innerApiError?.[key].type === ErrorPriority.high,
      );

      const resInnerApiError = { ...errorStore.innerApiError, [id]: innerApiError } as AppApiErrorList;

      if (highPriorityErrorKey && highPriorityErrorKey !== String(id)) {
        delete resInnerApiError[highPriorityErrorKey];
      }

      errorStore.innerApiError = resInnerApiError;
    }

    if (prevResolveRetry) {
      prevResolveRetry();
    }
    /**
     * Auto-retry mechanism was disabled - below code should be uncommented if auto-retry is needed
     */

    // const dmsStore = container.resolve<DmsStore>('dmsStore');

    // if (
    //   !skipRetry &&
    //   type === ErrorPriority.high &&
    //   errorStore.innerApiError &&
    //   errorStore.innerApiError[id] &&
    //   (dmsStore.dms ? dmsStore.dms?.consecutiveRequestFailuresLimit : 3) >= errorStore.innerApiError[id].retryAttempts
    // ) {
    //   errorStore.innerApiError[id].retryTimeout = setTimeout(onRetry, API_RETRY_TIMEOUT);
    // }
  });
}

/**
 * Decorator factory function
 * @category Utils
 */
export const Catch = ({
  Error,
  callback,
  type = ErrorPriority.medium,
  skipRetry = false,
  aliasMethod,
  aliasModule,
}: ErrorHandlingArgs): any => {
  return (target: any, propertyKey: string, descriptor: PropertyDescriptor) => {
    if (!descriptor || !descriptor.value) return;

    // Save a reference to the original method
    const originalMethod = descriptor.value;
    const argsCount = Reflect.getMetadata('design:paramtypes', target, propertyKey).length;
    const actionName = `${aliasModule || target.constructor.moduleName || target.constructor.name}.${
      aliasMethod || propertyKey
    }`;
    const moduleName = aliasModule || target.constructor.moduleName || target.constructor.name;

    // Rewrite original method with try/catch wrapper
    descriptor.value = function (...args: any[]) {
      const errorStore = container.resolve(ErrorStore);
      let id = Math.random();
      let isRetrying = false;

      if (args.length > 0) {
        const lastArg = String(args[args.length - 2]).split(':');
        if (lastArg[0] === 'apiErrorId') {
          id = Number(lastArg[1]);
          isRetrying = true;
        }
      }

      let isPromiseResult = false;

      try {
        if (!isRetrying) {
          errorStore.clearApiErrorsByLevel(ErrorPriority.medium);
        }

        const result = originalMethod.apply(this, args);
        // Check if method is asynchronous
        if (result && result instanceof Promise) {
          isPromiseResult = true;
          // Return promise
          return result
            .then((result) => {
              errorStore?.clearApiError(id);
              return result;
            })
            .catch((error: any) => {
              return _handleError(
                id,
                this,
                descriptor,
                args,
                Error,
                error,
                type,
                argsCount,
                actionName,
                moduleName,
                callback,
                skipRetry,
                isRetrying ? args?.[args.length - 1] : undefined,
              );
            })
            .finally(() => {
              if (isRetrying && args?.[args.length - 1]) {
                args?.[args.length - 1]();
              }
            });
        }
        // Return actual result
        errorStore?.clearApiError(id);
        return result;
      } catch (error: any) {
        return _handleError(
          id,
          this,
          descriptor,
          args,
          Error,
          error,
          type,
          argsCount,
          actionName,
          moduleName,
          callback,
          skipRetry,
        );
      } finally {
        if (isRetrying && !isPromiseResult && args?.[args.length - 1]) {
          args?.[args.length - 1]();
        }
      }
    };
    return descriptor;
  };
};

interface ErrorHandlingArgs {
  Error?: any;
  type?: ErrorPriority;
  skipRetry?: boolean;
  callback?: HandlerFunction;
  aliasMethod?: string;
  aliasModule?: string;
}

/**
 * Decorator function
 * @category Utils
 *
 * @param type priority or the error. Enumerated value: low, medium, hight etc.
 * @param skipRetry
 * @param callback error callback
 * @param aliasMethod
 * @param aliasModule
 */
export const errorHandling = ({ type, skipRetry, callback, aliasMethod, aliasModule }: ErrorHandlingArgs = {}): any =>
  Catch({ Error, callback, type, skipRetry, aliasMethod, aliasModule });

export function assertPresence<T>(resource: T, s = 'resource is undefined'): NonNullable<T> {
  if (resource == undefined) {
    throw new Error(s);
  }

  return resource as NonNullable<T>;
}
